import {observer} from "mobx-react";
import {useContext} from "react";
import {
  alpha,
  Button,
  Chip,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ClearIcon from "@mui/icons-material/Clear";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import PublicIcon from "@mui/icons-material/Public";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import RemoveIcon from "@mui/icons-material/Remove";
import useWave from "use-wave";
import {Frame} from "../common/Frame";
import {RecordingStoreContext} from "../../stores/RecordingStore";
import {AppContext} from "../../stores/AppStore";
import {useMobx} from "../../hooks/useMobx";
import {recording} from "../../proto/compiled";
import {D} from "../../Constants";

interface RecordingAnnotationItemProps {
  annotation: recording.IRecordingAnnotation;
  videoRef: React.RefObject<HTMLVideoElement | null>;
}

const formatTimestamp = (ms?: number | null) => {
  if (ms === undefined || ms === null || ms < 0) {
    return "—";
  }
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) {
    return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  }
  return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
};

export const RecordingAnnotationItem = observer(({annotation, videoRef}: RecordingAnnotationItemProps) => {
  const store = useContext(RecordingStoreContext);
  const app = useContext(AppContext);
  const theme = useTheme();
  const wave = useWave();
  const publishedWave = useWave();
  const localState = useMobx(() => ({
    hover: false,
  }));

  if (!store || !app) {
    return null;
  }

  const isSelected = store.selectedAnnotation === annotation;
  const isEditing = store.editingAnnotation === annotation;
  const isSaving = store.savingAnnotation === annotation;
  const endSet = annotation.endMs !== undefined && annotation.endMs !== null && annotation.endMs > (annotation.startMs ?? 0);

  const handleClick = () => {
    // Prevent selection changes while any annotation is being edited
    if (store.editingAnnotation) {
      return;
    }
    // Toggle selection - if already selected, deselect it
    if (isSelected) {
      store.selectedAnnotation = undefined;
    } else {
      store.selectedAnnotation = annotation;
      if (videoRef.current) {
        const startSeconds = Math.max(0, (annotation.startMs ?? 0) / 1000);
        videoRef.current.currentTime = startSeconds;
      }
    }
  };

  const handleCancel = async (event: React.MouseEvent) => {
    event.stopPropagation();
    // If the annotation has no ID, it's a new unsaved annotation - delete it
    if (!annotation.id || annotation.id.length === 0) {
      await store.deleteAnnotation(annotation);
    }
    store.editingAnnotation = undefined;
  };

  const handleSave = async (event: React.MouseEvent) => {
    event.stopPropagation();
    await store.saveAnnotation(annotation);
  };

  return (
    <Frame
      ref={wave}
      direction="column"
      gap={1.5}
      border={2}
      radius={1.5}
      padding={2}
      clickable={!isEditing}
      clip
      noTransition
      hover={!isSelected && !isEditing ? theme.palette.action.hover : undefined}
      background={isSelected ? theme.palette.background.paper : 'transparent'}
      style={{
        borderColor: isSelected
          ? theme.palette.primary.main
          : 'transparent',
        transition: 'all 0.2s ease-in-out',
        boxShadow: isSelected
          ? `0 2px 8px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.1)'}`
          : undefined,
        outline: !isSelected ? `1px solid ${theme.palette.divider}` : undefined,
        outlineOffset: '-2px',
        position: 'relative',
      }}
      onClick={handleClick}
      onPointerDownCapture={(event) => {
        if (isEditing) {
          event.stopPropagation();
        }
      }}
      onMouseEnter={() => {
        localState.hover = true;
      }}
      onMouseLeave={() => {
        localState.hover = false;
      }}
    >
      {/* Header with timestamps and actions */}
      <Frame
        row
        alignItems="center"
        justifyContent="space-between"
        marginBottom={0.5}
        style={{cursor: isEditing ? 'default' : 'pointer'}}
      >
        <Frame row gap={0.8} alignItems="center" style={{cursor: isEditing ? 'default' : 'pointer'}}>
          <Chip
            label={formatTimestamp(annotation.startMs)}
            size="small"
            color="primary"
            variant={isSelected ? "filled" : "outlined"}
          />
          {endSet && (
            <>
              <Typography variant="caption" color="text.secondary">→</Typography>
              <Chip
                label={formatTimestamp(annotation.endMs)}
                size="small"
                color="primary"
                variant={isSelected ? "filled" : "outlined"}
              />
            </>
          )}
        </Frame>

        {!isEditing && (
          <Frame row gap={0.5} alignItems="center">
            <IconButton
              size="small"
              aria-label="Edit annotation"
              onClick={(event) => {
                event.stopPropagation();
                store.editingAnnotation = annotation;
                store.selectedAnnotation = annotation;
              }}
              onPointerDownCapture={(event) => {
                event.stopPropagation();
              }}
            >
              <EditIcon fontSize="small"/>
            </IconButton>
            <IconButton
              size="small"
              aria-label="Delete annotation"
              color="error"
              onClick={(event) => {
                event.stopPropagation();
                app.confirmationDialogAction = async () => {
                  await store.deleteAnnotation(annotation);
                };
                app.confirmationDialogText = 'Are you sure you want to delete this annotation?';
              }}
              onPointerDownCapture={(event) => {
                event.stopPropagation();
              }}
            >
              <DeleteIcon fontSize="small"/>
            </IconButton>
          </Frame>
        )}
      </Frame>

      {/* Content area - different rendering for edit vs read mode */}
      {isEditing ? (
        <>
          <TextField
            label="Label"
            size="small"
            fullWidth
            value={annotation.label ?? ""}
            onChange={(event) => {
              annotation.label = event.target.value;
            }}
            autoFocus
          />
          <TextField
            label="Description"
            size="small"
            fullWidth
            multiline
            minRows={3}
            maxRows={8}
            value={annotation.description ?? ""}
            onChange={(event) => {
              annotation.description = event.target.value;
            }}
          />

          {/* Annotation Type Selector */}
          <AnnotationTypeSelector
            value={annotation.annotationType ?? recording.AnnotationType.AT_NONE}
            onChange={(type) => {
              annotation.annotationType = type;
            }}
          />

          <Frame
            ref={publishedWave}
            row
            gap={1}
            padding={1.5}
            radius={1}
            style={{
              backgroundColor: annotation.published
                ? theme.palette.success.main + '15'
                : theme.palette.action.hover,
              border: `2px solid ${annotation.published ? theme.palette.success.main : theme.palette.divider}`,
              cursor: 'pointer',
              transition: 'all 0.2s ease-in-out',
              height: D.interactive(),
              userSelect: 'none',
              overflow: 'hidden',
            }}
            onClick={(event) => {
              event.stopPropagation();
              annotation.published = !annotation.published;
            }}
            alignItems="center"
          >
            {annotation.published ? (
              <PublicIcon color="success" fontSize="small"/>
            ) : (
              <VisibilityOffIcon color="disabled" fontSize="small"/>
            )}
            <Typography variant="body2" fontWeight={500} style={{flex: 1, userSelect: 'none'}}>
              {annotation.published ? 'Published' : 'Not published'}
            </Typography>
          </Frame>
          <Frame row gap={1}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AccessTimeIcon/>}
              onClick={(event) => {
                event.stopPropagation();
                annotation.startMs = Math.round(store.currentTimeMs);
              }}
              disabled={endSet && store.currentTimeMs > (annotation.endMs ?? 0)}
              fullWidth
              style={{userSelect: 'none'}}
            >
              Set start
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={endSet ? <ClearIcon/> : <AccessTimeIcon/>}
              onClick={(event) => {
                event.stopPropagation();
                if (endSet) {
                  annotation.endMs = undefined;
                } else {
                  const calculated = Math.round(store.currentTimeMs);
                  const start = annotation.startMs ?? 0;
                  annotation.endMs = Math.max(calculated, start);
                }
              }}
              disabled={!endSet && store.currentTimeMs < (annotation.startMs ?? 0)}
              fullWidth
              style={{userSelect: 'none'}}
              color={endSet ? "error" : undefined}
            >
              {endSet ? 'Clear end' : 'Set end'}
            </Button>
          </Frame>

          {/* Save/Cancel buttons at bottom */}
          <Frame row gap={1.5} marginTop={0.5}>
            <Button
              variant="contained"
              color="primary"
              size="medium"
              startIcon={<SaveIcon/>}
              onClick={handleSave}
              disabled={isSaving}
              fullWidth
            >
              Save
            </Button>
            <Button
              variant="outlined"
              size="medium"
              startIcon={<CancelIcon/>}
              onClick={handleCancel}
              disabled={isSaving}
              fullWidth
            >
              Cancel
            </Button>
          </Frame>
        </>
      ) : (
        <>
          {/* Read-only view with proper typography */}
          <Frame direction="column" gap={1.5}>
            {annotation.label && (
              <Typography variant="body1" fontWeight={600}>
                {annotation.label}
              </Typography>
            )}

            {annotation.description && (
              <Typography variant="body2" color="text.secondary" style={{whiteSpace: 'pre-wrap'}}>
                {annotation.description}
              </Typography>
            )}

            {!annotation.label && !annotation.description && (
              <Typography variant="body2" color="text.secondary" fontStyle="italic">
                No label or description set
              </Typography>
            )}

            <Frame row alignItems="center" justifyContent="space-between">
              <Frame row alignItems="center" gap={1}>
                {annotation.published ? (
                  <CheckCircleIcon fontSize="small" color="success"/>
                ) : (
                  <RadioButtonUncheckedIcon fontSize="small" color="disabled"/>
                )}
                <Typography variant="body2" color="text.secondary">
                  {annotation.published ? "Published" : "Not published"}
                </Typography>
              </Frame>
              {/* Annotation Type Badge */}
              <AnnotationTypeBadge annotationType={annotation.annotationType} />
            </Frame>
          </Frame>
        </>
      )}
    </Frame>
  );
});

// Annotation Type Selector Component
interface AnnotationTypeSelectorProps {
  value: recording.AnnotationType;
  onChange: (type: recording.AnnotationType) => void;
}

const AnnotationTypeSelector = observer(({value, onChange}: AnnotationTypeSelectorProps) => {
  const theme = useTheme();

  const options = [
    {
      value: recording.AnnotationType.AT_GOOD_PRACTICE,
      label: 'Good',
      color: theme.palette.success.main,
      icon: <CheckIcon fontSize="small" />,
    },
    {
      value: recording.AnnotationType.AT_COMMON_MISTAKE,
      label: 'Bad',
      color: theme.palette.error.main,
      icon: <CloseIcon fontSize="small" />,
    },
    {
      value: recording.AnnotationType.AT_NONE,
      label: 'Neutral',
      color: theme.palette.grey[600],
      icon: <RemoveIcon fontSize="small" />,
    },
  ];

  return (
    <Frame direction="column" gap={0.5}>
      <Typography variant="caption" color="textSecondary">
        Example type
      </Typography>
      <Frame row gap={1} wrap>
        {options.map((option) => {
          const isSelected = value === option.value;
          return (
            <Frame
              key={option.value}
              row
              gap={0.5}
              clickable
              paddingH={1.5}
              radius={0.75}
              alignItems="center"
              justifyContent="center"
              style={{
                border: `2px solid ${isSelected ? option.color : theme.palette.divider}`,
                backgroundColor: isSelected ? alpha(option.color, 0.1) : 'transparent',
                transition: 'all 0.2s ease-in-out',
                height: D.interactive(),
                flex: 1,
              }}
              onClick={(e) => {
                e.stopPropagation();
                onChange(option.value);
              }}
            >
              <Frame row alignItems="center" style={{color: isSelected ? option.color : theme.palette.text.secondary}}>
                {option.icon}
              </Frame>
              <Typography
                variant="body2"
                style={{
                  color: isSelected ? option.color : theme.palette.text.secondary,
                  fontWeight: isSelected ? 600 : 400,
                  userSelect: 'none',
                }}
              >
                {option.label}
              </Typography>
            </Frame>
          );
        })}
      </Frame>
    </Frame>
  );
});

// Annotation Type Badge Component for read mode
interface AnnotationTypeBadgeProps {
  annotationType?: recording.AnnotationType | null;
}

const AnnotationTypeBadge = ({annotationType}: AnnotationTypeBadgeProps) => {
  const theme = useTheme();

  // Show nothing for none or undefined types
  if (annotationType === undefined || annotationType === null || annotationType === recording.AnnotationType.AT_NONE) {
    return null;
  }

  const isGoodPractice = annotationType === recording.AnnotationType.AT_GOOD_PRACTICE;
  const color = isGoodPractice ? theme.palette.success.main : theme.palette.error.main;
  const label = isGoodPractice ? "Good Practice" : "Common Mistake";
  const Icon = isGoodPractice ? CheckIcon : CloseIcon;

  return (
    <Frame
      row
      gap={0.5}
      alignItems="center"
    >
      <Icon fontSize="small" style={{color}} />
      <Typography
        variant="body2"
        fontWeight="bold"
        style={{color}}
      >
        {label}
      </Typography>
    </Frame>
  );
};
